import {Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {finalize} from 'rxjs';
import {NzAlertComponent} from 'ng-zorro-antd/alert';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {AuthFlowService} from '../../../core/services/auth-flow.service';
import {PsdFields} from '../../../shared/psd-fields/psd-fields';
import {createPsdForm} from '../../../shared/psd-fields/psd-form';

export interface ExpiredPsdModalData {
  token: string;
  temporary: boolean;
}

@Component({
  selector: 'app-expired-psd-modal',
  imports: [
    ReactiveFormsModule,
    NzAlertComponent,
    NzFormModule,
    TranslatePipe,
    PsdFields,
  ],
  templateUrl: './expired-psd-modal.html',
})
export class ExpiredPsdModal {
  private readonly _data = inject<ExpiredPsdModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly temporary = this._data.temporary;

  readonly form = createPsdForm();

  constructor() {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._modalRef.updateConfig({nzOkDisabled: this.form.invalid}));
  }

  handleOk(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const {newPsd, confirmPsd} = this.form.getRawValue();
    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._authFlow.resetPsd({token: this._data.token, newPsd, confirmPsd})
      .pipe(finalize(() => this._modalRef.updateConfig({nzOkLoading: false})), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._modalRef.close(newPsd);
          this._notification.success(
            this._translate.instant('expiredPsd.changedTitle'),
            this._translate.instant('expiredPsd.changedDesc')
          );
        },
        error: () => {
          this._modalRef.updateConfig({nzCancelDisabled: false});
        }
      });
  }
}
